import { t } from "@travily/i18n";
import familyIllustration from "@ui/assets/illustrations/family-animated.webp";
import { ResponsiveText } from "@ui/components/ResponsiveText";
import { TileRadioGroup } from "@ui/components/TileRadioGroup";
import { Wizard } from "@ui/components/Wizard";
import { useBreakpoint } from "@ui/hooks/use-breakpoint";
import type { ReactNode } from "react";

const COLOR = "red" as const;

export interface SubscribeStepViewProps {
  /** Current stage, 1-indexed. */
  currentStage: number;
  /** Total stages, drives the progress bar. */
  totalStages: number;
  /** Whether the primary submit is in-flight. */
  primaryPending: boolean;
  /** Current newsletter-consent selection, or `null` when unset. */
  newsletterConsent: "yes" | "no" | null;
  /** Field-level validation error, when present. */
  error?: string;
  /** Top-bar tertiary actions (e.g. "Questions?"). */
  topBarActions: ReactNode;
  /** Back affordance handler; omitted on the first stage. */
  onBack?: () => void;
  /** Advance the wizard. */
  onPrimary: () => void;
  /** Select a newsletter-consent option. */
  onSelect: (value: "yes" | "no") => void;
}

export const SubscribeStepView = ({
  currentStage,
  totalStages,
  primaryPending,
  newsletterConsent,
  error,
  topBarActions,
  onBack,
  onPrimary,
  onSelect,
}: SubscribeStepViewProps) => {
  const isAboveMd = useBreakpoint("md");
  const isAboveLg = useBreakpoint("lg");
  const selectionGroupSize = isAboveLg ? "5xl" : isAboveMd ? "4xl" : "3xl";

  return (
    <Wizard color={COLOR} currentStage={currentStage} totalStages={totalStages} onBack={onBack}>
      {topBarActions}
      <Wizard.PrimaryButton
        isPending={primaryPending}
        mobileLabel={t("web.founding_members.creator.steps.family.subscribe.show.button.base")}
        onPress={onPrimary}
      >
        {t("web.founding_members.creator.steps.family.subscribe.show.button.md")}
      </Wizard.PrimaryButton>
      <Wizard.Stage
        illustration={<img alt="" aria-hidden="true" className="h-28 w-28 object-contain" src={familyIllustration} />}
        heading={t("web.founding_members.creator.steps.family.subscribe.show.title")}
        helper={t("web.founding_members.creator.steps.family.subscribe.show.subtitle")}
      >
        <TileRadioGroup
          className="w-full max-w-[600px]"
          color={COLOR}
          onChange={(value) => onSelect(value as "yes" | "no")}
          size={selectionGroupSize}
          value={newsletterConsent ?? undefined}
        >
          <TileRadioGroup.Item value="yes">
            <ResponsiveText
              base={t("web.founding_members.creator.steps.family.subscribe.show.options.yes.base")}
              sm={t("web.founding_members.creator.steps.family.subscribe.show.options.yes.sm")}
            />
          </TileRadioGroup.Item>
          <TileRadioGroup.Item value="no">
            <ResponsiveText
              base={t("web.founding_members.creator.steps.family.subscribe.show.options.no.base")}
              sm={t("web.founding_members.creator.steps.family.subscribe.show.options.no.sm")}
            />
          </TileRadioGroup.Item>
        </TileRadioGroup>
        {error ? <Wizard.FieldError>{error}</Wizard.FieldError> : null}
      </Wizard.Stage>
    </Wizard>
  );
};
